import React, { useState } from 'react';

import {
  SpaceBarOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignCenterOutlined,
  VerticalAlignTopOutlined,
} from '@mui/icons-material';
import { Columns } from 'lucide-react';

import ColumnsContainerPropsSchema, {
  ColumnsContainerProps,
} from '../../../../documents/blocks/ColumnsContainer/ColumnsContainerPropsSchema';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import ColumnWidthsInput from './helpers/inputs/ColumnWidthsInput';
import RadioGroupInput from './helpers/inputs/RadioGroupInput';
import SliderInput from './helpers/inputs/SliderInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';
import { Separator } from '@/components/ui/separator';
import { ToggleGroupItem } from '@/components/ui/toggle-group';

type ColumnsContainerPanelProps = {
  data: ColumnsContainerProps;
  setData: (v: ColumnsContainerProps) => void;
};

export default function ColumnsContainerPanel({ data, setData }: ColumnsContainerPanelProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);
  const updateData = (d: unknown) => {
    const res = ColumnsContainerPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  return (
    <BaseSidebarPanel title="Columns block">
      {/* COLUMNS SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Columns}>Columns</SectionHeader>
        <div className="space-y-3">
          <RadioGroupInput
            label="Number of columns"
            defaultValue={data.props?.columnsCount === 2 ? '2' : '3'}
            onChange={(v) => {
              updateData({ ...data, props: { ...data.props, columnsCount: v === '2' ? 2 : 3 } });
            }}
          >
            <ToggleGroupItem value="2">2</ToggleGroupItem>
            <ToggleGroupItem value="3">3</ToggleGroupItem>
          </RadioGroupInput>
          <ColumnWidthsInput
            defaultValue={data.props?.fixedWidths}
            onChange={(fixedWidths) => {
              updateData({ ...data, props: { ...data.props, fixedWidths } });
            }}
          />
          <SliderInput
            label="Columns gap"
            iconLabel={<SpaceBarOutlined />}
            units="px"
            step={4}
            marks
            min={0}
            max={80}
            defaultValue={data.props?.columnsGap ?? 0}
            onChange={(columnsGap) => updateData({ ...data, props: { ...data.props, columnsGap } })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <RadioGroupInput
            label="Alignment"
            defaultValue={data.props?.contentAlignment ?? 'middle'}
            onChange={(contentAlignment) => {
              updateData({ ...data, props: { ...data.props, contentAlignment } });
            }}
          >
            <ToggleGroupItem value="top">
              <VerticalAlignTopOutlined fontSize="small" />
            </ToggleGroupItem>
            <ToggleGroupItem value="middle">
              <VerticalAlignCenterOutlined fontSize="small" />
            </ToggleGroupItem>
            <ToggleGroupItem value="bottom">
              <VerticalAlignBottomOutlined fontSize="small" />
            </ToggleGroupItem>
          </RadioGroupInput>
          <MultiStylePropertyPanel
            names={['backgroundColor', 'padding']}
            value={data.style}
            onChange={(style) => updateData({ ...data, style })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
